import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { Auth0Context } from "react-auth0-spa";
import axios from "axios";
import Logo from "assets/Color.svg";
import "./UnverifiedUser.sass";

const RESEND_ERROR_MESSAGE =
  "We couldn't send a new link. Please try again in a few minutes, or contact your Cortica care team.";
const SIGN_IN_EXPIRED_MESSAGE =
  "Your sign-in has expired. Please choose Back to Log In, sign in again, then press Resend.";
const STILL_UNVERIFIED_MESSAGE =
  "We can't see your verification yet. Please click the link in the email, then try again.";
const RECHECK_ERROR_MESSAGE =
  "We couldn't check your verification right now. Please try again, or go back to log in and sign in again.";

// Continue may send the family through Auth0 sign-in; they come back here with this flag,
// so a family who still hasn't clicked the link is told why they're on the same page.
const RECHECKED_PARAM = "rechecked";
const RECHECK_RETURN_PATH = `/unverified_user?${RECHECKED_PARAM}=1`;

// The card stays on screen while an action runs, so screen readers announce the status change
const BUSY_ACTION = { RESEND: "resend", CONTINUE: "continue" };

// Custom withRouter HOC for React Router v6
function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let params = useParams();

    return <Component {...props} location={location} params={params} />;
  }
  
  return ComponentWithRouterProp;
}

class UnverifiedUser extends React.Component {
  state = {
    errorMessage: new URLSearchParams(this.props.location.search).has(RECHECKED_PARAM)
      ? STILL_UNVERIFIED_MESSAGE
      : null,
    emailResent: false,
    busyAction: null,
  };

  componentDidMount() {
    window.addEventListener("pageshow", this.resetAfterBackNavigation);
  }

  componentWillUnmount() {
    window.removeEventListener("pageshow", this.resetAfterBackNavigation);
  }

  // Pressing Back on the Auth0 sign-in page can restore this page from the browser's
  // back/forward cache with Continue still busy; give the family their buttons back.
  resetAfterBackNavigation = (event) => {
    if (event.persisted) this.setState({ busyAction: null });
  };

  getUserEmail = () => {
    const { user } = this.context;
    return user.email;
  };

  resendVerificationEmail = async () => {
    this.setState({ busyAction: BUSY_ACTION.RESEND, errorMessage: null });
    const { getTokenSilently } = this.context;
    const url = `${process.env.REACT_APP_BACKEND_API}/resendVerificationEmail`;
    let token;
    try {
      token = await getTokenSilently();
    } catch (error) {
      // Renewing an expired sign-in needs an MFA code, so it fails here (and signs the family out)
      console.error(error);
      this.setState({
        errorMessage: SIGN_IN_EXPIRED_MESSAGE,
        emailResent: false,
        busyAction: null,
      });
      return;
    }
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      await axios.post(url, null, config);
      this.setState({
        emailResent: true,
        busyAction: null,
      });
    } catch (error) {
      console.error(error);
      this.setState({
        errorMessage: RESEND_ERROR_MESSAGE,
        emailResent: false,
        busyAction: null,
      });
    }
  };

  continueAfterVerification = async () => {
    this.setState({ busyAction: BUSY_ACTION.CONTINUE, errorMessage: null });
    const { recheckVerification } = this.context;
    try {
      const { isVerified, isRedirecting } = await recheckVerification({
        returnTo: RECHECK_RETURN_PATH,
      });
      // render() sends a verified user on; Continue stays busy during the hand-over to Auth0 sign-in
      if (isVerified || isRedirecting) return;
      this.setState({
        errorMessage: STILL_UNVERIFIED_MESSAGE,
        busyAction: null,
      });
    } catch (error) {
      console.error(error);
      this.setState({
        errorMessage: RECHECK_ERROR_MESSAGE,
        busyAction: null,
      });
    }
  };

  handleLogout = () => {
    const { logout } = this.context;
    logout();
  };

  // Redirect while rendering: react-router ignores navigate() calls made before the
  // withRouter wrapper's effects have run, which includes this component's componentDidMount.
  render() {
    const { user } = this.context;
    if (!user) return <Navigate to="/login" replace />;
    if (user.email_verified) return <Navigate to="/" replace />;

    const { errorMessage, emailResent, busyAction } = this.state;
    const isBusy = busyAction !== null;

    return (
      <section className="verifyemail-wrapper">
        <div className="verifyemail">
          <div className="img-wrapper">
            <img src={Logo} alt="Cortica logo" />
          </div>
          <div className="text-wrapper">
            <p className="verifyemail-title">Verify Your Email Address</p>
            {errorMessage && (
              <p className="verifyemail-error" role="alert">
                {errorMessage}
              </p>
            )}
            <p className="verifyemail-instructions" role="status">
              {emailResent ? (
                <>
                  We've sent a new link to {this.getUserEmail()}.
                  <br />
                  Please click the link in that email to verify your email
                  address.
                </>
              ) : (
                <>
                  An email has been sent to {this.getUserEmail()}.
                  <br />
                  Please click on the link in your email to verify your email
                  address.
                </>
              )}
            </p>
            <p className="verifyemail-resend">
              Didn't get an email?
              <button onClick={this.resendVerificationEmail} disabled={isBusy}>
                {busyAction === BUSY_ACTION.RESEND ? " Sending…" : " Resend"}
              </button>
            </p>
            <button
              className="verifyemail-continue"
              onClick={this.continueAfterVerification}
              disabled={isBusy}
            >
              {busyAction === BUSY_ACTION.CONTINUE
                ? "Checking…"
                : "I've verified — continue"}
            </button>
            <button onClick={this.handleLogout} disabled={isBusy}>
              Back to Log In
            </button>
          </div>
        </div>
      </section>
    );
  }
}

UnverifiedUser.contextType = Auth0Context;

export default withRouter(UnverifiedUser);
